import { forwardRef, type ComponentType, type ReactNode } from 'react';
import { SlidersHorizontal, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MobileFilterPillButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: ReactNode;
  icon?: LucideIcon | ComponentType<{ className?: string }>;
  trailingIcon?: LucideIcon | ComponentType<{ className?: string }>;
  count?: number;
  active?: boolean;
}

/**
 * Standard mobile toolbar pill button used as a sheet trigger for Filters / Sort.
 *
 * Visual contract:
 *  - h-9 rounded-full px-3.5 text-[13px]
 *  - bg-white/[0.05] by default
 *  - bg-active-gold/95 text-black when `active` (filter count > 0 or non-default sort)
 *  - optional count chip on the right
 */
export const MobileFilterPillButton = forwardRef<HTMLButtonElement, MobileFilterPillButtonProps>(
  (
    { label, icon: Icon = SlidersHorizontal, trailingIcon: TrailingIcon, count, active, className, ...rest },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        {...rest}
        className={cn(
          'shrink-0 inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-[13px] whitespace-nowrap border transition-colors',
          active
            ? 'bg-active-gold/10 text-active-gold font-medium ring-1 ring-active-gold/30 border-transparent'
            : 'bg-black/30 border-white/[0.08] text-foreground hover:border-white/20',
          className,
        )}
      >
        <Icon className="h-4 w-4" />
        <span>{label}</span>
        {typeof count === 'number' && count > 0 && (
          <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-black/20 text-[10px]">
            {count}
          </span>
        )}
        {TrailingIcon && <TrailingIcon className="h-4 w-4 opacity-70" />}
      </button>
    );
  },
);

MobileFilterPillButton.displayName = 'MobileFilterPillButton';
